import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import {
  deriveSpecies,
  matchesSpeciesFilter,
  type Camera,
  type Shot,
  type Species,
  type SpeciesFilterKey,
} from "../api/types";
import { wsBus } from "../api/wsBus";
import { AllChip, CameraChip } from "../components/Chips";
import { AnimateButton } from "../components/AnimationDialog";
import { ImageCard } from "../components/ImageCard";
import { Lightbox } from "../components/Lightbox";
import { SpeciesFilter } from "../components/SpeciesFilter";
import {
  addDays,
  endOfDay,
  endOfMonth,
  sameDay,
  SPECIES_LABEL,
  SPECIES_ORDER,
  startOfDay,
  startOfMonth,
} from "../components/format";

interface Props {
  selectedDate: Date;
  focusImageId?: string | null;
  onSelectDate: (d: Date) => void;
  onFocusConsumed?: () => void;
}

export function CalendarScreen({
  selectedDate,
  focusImageId,
  onSelectDate,
  onFocusConsumed,
}: Props) {
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(selectedDate));
  const [monthShots, setMonthShots] = useState<Shot[]>([]);
  const [dayShots, setDayShots] = useState<Shot[]>([]);
  const [totalKnown, setTotalKnown] = useState<number>(0);
  const [cameras, setCameras] = useState<Camera[]>([]);
  // Filter survives day navigation because this component stays mounted across
  // selectedDate changes.
  const [filter, setFilter] = useState<"all" | string>("all");
  const [speciesFilter, setSpeciesFilter] = useState<Set<SpeciesFilterKey>>(
    () => new Set(["moose"]),
  );
  // Image currently flashing to mark a bell-driven landing. Cleared when the
  // animation finishes so the highlight only fires once per navigation.
  const [flashId, setFlashId] = useState<string | null>(null);
  // Lightbox state lives here (not inside ImageCard) so keyboard navigation
  // can hop between cards and across day boundaries.
  const [openShotId, setOpenShotId] = useState<string | null>(null);
  // When a page-up/down step lands on a new day, we don't know which shot to
  // open until that day's shots load. Setting "first" / "last" lets the
  // dayShots-watching effect pick the right one once the data arrives.
  const [pendingJump, setPendingJump] = useState<"first" | "last" | null>(null);

  const toggleSpecies = useCallback((k: SpeciesFilterKey) => {
    setSpeciesFilter((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);

  // Keep the visible month cursor in sync with the selected date so /calendar?d=...
  // deep-links land on the right grid.
  useEffect(() => {
    setCursor((c) => {
      const m = startOfMonth(selectedDate);
      return c.getTime() === m.getTime() ? c : m;
    });
  }, [selectedDate]);

  const loadMonth = useCallback(async () => {
    const from = startOfMonth(cursor).toISOString();
    const to = endOfMonth(cursor).toISOString();
    const page = await api.listImages({ from, to, limit: 200 });
    setMonthShots(page.items);
    setTotalKnown((t) => Math.max(t, page.items.length));
  }, [cursor]);

  const loadDay = useCallback(async () => {
    const from = startOfDay(selectedDate).toISOString();
    const to = endOfDay(selectedDate).toISOString();
    const page = await api.listImages({ from, to, limit: 200 });
    setDayShots(page.items);
  }, [selectedDate]);

  useEffect(() => {
    void loadMonth();
  }, [loadMonth]);

  useEffect(() => {
    void loadDay();
  }, [loadDay]);

  useEffect(() => {
    void api.listCameras().then(setCameras);
  }, []);

  useEffect(() => {
    return wsBus.subscribe(() => {
      void loadMonth();
      void loadDay();
    });
  }, [loadMonth, loadDay]);

  // Bell-driven focus: once the day's shots load, widen any filters that
  // would hide the target, scroll the card into view, and flash a highlight.
  // Then clear the `img` param from the URL so revisits don't re-trigger.
  useEffect(() => {
    if (!focusImageId) return;
    const target = dayShots.find((s) => s.imageId === focusImageId);
    if (!target) return; // not in this day's window yet — wait for next load

    if (filter !== "all" && filter !== target.cameraId) setFilter("all");

    const sp = deriveSpecies(target.analysis);
    const key: SpeciesFilterKey =
      sp === "moose" || sp === "roe-deer" || sp === "wild-boar" ? sp : "other";
    if (!speciesFilter.has(key)) {
      setSpeciesFilter((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    }

    setFlashId(focusImageId);
    const id = focusImageId;
    // Defer to the next frame so the card is actually mounted (especially
    // when filter widening just unhid it).
    const raf = window.requestAnimationFrame(() => {
      const el = document.getElementById(`shot-${id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    onFocusConsumed?.();

    return () => window.cancelAnimationFrame(raf);
    // Intentionally exclude filter/speciesFilter: we only want to react to a
    // new focusImageId or fresh dayShots load, not to filter changes the
    // effect itself triggered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusImageId, dayShots]);

  // Clear the highlight once the CSS flash has run its course. Keyed off
  // flashId (not focusImageId) so the timer survives onFocusConsumed clearing
  // the URL param.
  useEffect(() => {
    if (!flashId) return;
    const t = window.setTimeout(() => setFlashId(null), 2400);
    return () => window.clearTimeout(t);
  }, [flashId]);

  const onShotChanged = useCallback((updated: Shot) => {
    const replace = (s: Shot) => (s.imageId === updated.imageId ? updated : s);
    setMonthShots((prev) => prev.map(replace));
    setDayShots((prev) => prev.map(replace));
  }, []);

  const onShotHidden = useCallback((imageId: string) => {
    const drop = (s: Shot) => s.imageId !== imageId;
    setMonthShots((prev) => prev.filter(drop));
    setDayShots((prev) => prev.filter(drop));
  }, []);

  const cameraScopedMonth = useMemo(
    () => (filter === "all" ? monthShots : monthShots.filter((s) => s.cameraId === filter)),
    [monthShots, filter],
  );
  const cameraScopedDay = useMemo(
    () => (filter === "all" ? dayShots : dayShots.filter((s) => s.cameraId === filter)),
    [dayShots, filter],
  );

  const visibleMonthShots = useMemo(
    () => cameraScopedMonth.filter((s) => matchesSpeciesFilter(s.analysis, speciesFilter)),
    [cameraScopedMonth, speciesFilter],
  );
  const visibleDayShots = useMemo(
    () => cameraScopedDay.filter((s) => matchesSpeciesFilter(s.analysis, speciesFilter)),
    [cameraScopedDay, speciesFilter],
  );

  // Counts under each species chip reflect the camera scope but ignore the
  // species filter itself — so an unchecked chip can still show how many
  // frames would unlock if you tick it. Bucketed by the chip a card would
  // display, identical to the matcher's logic.
  const speciesFilterCounts = useMemo(() => {
    const m: Record<SpeciesFilterKey, number> = {
      "moose": 0, "roe-deer": 0, "wild-boar": 0, "other": 0,
    };
    for (const s of cameraScopedDay) {
      const sp = deriveSpecies(s.analysis);
      if (sp === "moose") m["moose"]++;
      else if (sp === "roe-deer") m["roe-deer"]++;
      else if (sp === "wild-boar") m["wild-boar"]++;
      else if (sp === "other") m["other"]++;
      else if (sp === null && s.analysis.state === "done") m["other"]++;
    }
    return m;
  }, [cameraScopedDay]);

  const dayCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of visibleMonthShots) {
      const d = new Date(s.capturedAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [visibleMonthShots]);

  const chipCounts = useMemo(() => {
    const m: Record<string, number> = { all: dayShots.length };
    for (const c of cameras) m[c.cameraId] = dayShots.filter((s) => s.cameraId === c.cameraId).length;
    return m;
  }, [dayShots, cameras]);

  const days = useMemo(() => {
    const first = startOfMonth(cursor);
    const month = first.getMonth();
    const year = first.getFullYear();
    // Mon-first calendar: Sun=0 becomes 6, Mon=1 becomes 0, etc.
    const startDow = (first.getDay() + 6) % 7;
    const totalDays = new Date(year, month + 1, 0).getDate();
    type Cell = { empty: true } | { empty: false; date: Date; count: number };
    const cells: Cell[] = [];
    for (let i = 0; i < startDow; i++) cells.push({ empty: true });
    for (let d = 1; d <= totalDays; d++) {
      const dt = new Date(year, month, d);
      const key = `${year}-${month}-${d}`;
      cells.push({ empty: false, date: dt, count: dayCounts.get(key) ?? 0 });
    }
    return cells;
  }, [cursor, dayCounts]);

  const maxCount = useMemo(() => {
    let m = 0;
    for (const v of dayCounts.values()) if (v > m) m = v;
    return m || 1;
  }, [dayCounts]);

  const byHour = useMemo(() => {
    const m = new Map<number, Shot[]>();
    for (const s of visibleDayShots) {
      const h = new Date(s.capturedAt).getHours();
      if (!m.has(h)) m.set(h, []);
      m.get(h)!.push(s);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime());
    }
    return [...m.entries()].sort((a, b) => b[0] - a[0]);
  }, [visibleDayShots]);

  // Flattened in render order — matches the on-screen grid top-to-bottom,
  // so arrow-key navigation feels like reading the page.
  const displayedShots = useMemo(
    () => byHour.flatMap(([, list]) => list),
    [byHour],
  );

  const openIndex = openShotId
    ? displayedShots.findIndex((s) => s.imageId === openShotId)
    : -1;
  const openShot = openIndex >= 0 ? displayedShots[openIndex] : null;

  // Day-jump resolution: once dayShots reload after PageUp/Down, snap the
  // lightbox to the first or last visible frame of the new day. If the day
  // is empty (under the current filters), close the lightbox.
  useEffect(() => {
    if (!pendingJump) return;
    if (displayedShots.length === 0) {
      setOpenShotId(null);
      setPendingJump(null);
      return;
    }
    const pick =
      pendingJump === "first"
        ? displayedShots[0]
        : displayedShots[displayedShots.length - 1];
    setOpenShotId(pick.imageId);
    setPendingJump(null);
  }, [pendingJump, displayedShots]);

  const moveMonth = (dir: -1 | 1) => {
    setCursor((c) => {
      const d = new Date(c);
      d.setMonth(d.getMonth() + dir);
      return d;
    });
  };

  const stepDay = (dir: -1 | 1) => {
    onSelectDate(addDays(selectedDate, dir));
  };

  const today = new Date();
  const monthName = cursor.toLocaleDateString("en-US", { month: "long" });
  const yearStr = cursor.getFullYear();

  return (
    <div className="cal-shell">
      <aside className="cal">
        <div className="cal-hd">
          <div>
            <div className="month serif">{monthName}</div>
            <div className="year">{yearStr}</div>
          </div>
          <div className="cal-nav">
            <button onClick={() => moveMonth(-1)} aria-label="Previous month">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button onClick={() => moveMonth(1)} aria-label="Next month">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="cal-grid">
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            <div className="cal-dow" key={i}>
              {d}
            </div>
          ))}
          {days.map((cell, i) => {
            if (cell.empty) return <div className="cal-day empty" key={i} />;
            const isToday = sameDay(cell.date, today);
            const isSelected = sameDay(cell.date, selectedDate);
            const hasShots = cell.count > 0;
            const ratio = cell.count / maxCount;
            return (
              <button
                key={i}
                className={`cal-day ${hasShots ? "has-shots" : ""} ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
                style={
                  {
                    "--shots-bar": `${6 + ratio * 22}px`,
                    "--shots-op": `${0.25 + ratio * 0.75}`,
                  } as React.CSSProperties
                }
                onClick={() => onSelectDate(cell.date)}
                aria-label={`${cell.date.toDateString()} — ${cell.count} frame${cell.count === 1 ? "" : "s"}`}
              >
                {cell.date.getDate()}
              </button>
            );
          })}
        </div>

        <div className="cal-legend">
          <span>fewer</span>
          <span className="ramp" />
          <span>more</span>
        </div>

        <div
          style={{
            marginTop: "20px",
            paddingTop: "18px",
            borderTop: "1px solid var(--line)",
            fontFamily: "var(--mono)",
            fontSize: "0.66em",
            color: "var(--fg-mute)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>Month total</span>
          <span style={{ color: "var(--fg)" }}>{totalKnown.toLocaleString()}</span>
        </div>
      </aside>

      <main>
        <div className="day-feed-hd">
          <div className="date-bd serif">
            <span className="wkday">
              {selectedDate.toLocaleDateString("en-US", { weekday: "long" })}
            </span>
            {selectedDate.toLocaleDateString("en-US", { month: "long", day: "numeric" })}
            <span style={{ color: "var(--fg-mute)" }}>, {selectedDate.getFullYear()}</span>
          </div>
          <div className="jump">
            <button onClick={() => stepDay(-1)} aria-label="Previous day">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Prev day
            </button>
            <button onClick={() => stepDay(1)} aria-label="Next day">
              Next day
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <AnimateButton
              shots={visibleDayShots}
              sourceLabel={`calendar-${selectedDate.toISOString().slice(0, 10)}`}
            />
          </div>
        </div>

        <DaySummary shots={visibleDayShots} />

        {cameras.length > 0 && (
          <div className="cam-chips" style={{ marginBottom: "10px" }}>
            <AllChip active={filter === "all"} count={chipCounts.all} onClick={() => setFilter("all")} />
            {cameras.map((c) => (
              <CameraChip
                key={c.cameraId}
                camera={c}
                count={chipCounts[c.cameraId] ?? 0}
                active={filter === c.cameraId}
                onClick={() => setFilter(c.cameraId)}
              />
            ))}
          </div>
        )}
        <div style={{ marginBottom: "24px" }}>
          <SpeciesFilter
            selected={speciesFilter}
            counts={speciesFilterCounts}
            onToggle={toggleSpecies}
          />
        </div>

        {byHour.length === 0 ? (
          <div className="empty-day">
            <div className="glyph">Stillness.</div>
            <div>No images captured on this day.</div>
            <div style={{ marginTop: "20px", display: "flex", gap: "8px", justifyContent: "center" }}>
              <button className="chip" onClick={() => stepDay(-1)}>
                ← Previous day
              </button>
              <button className="chip" onClick={() => stepDay(1)}>
                Next day →
              </button>
            </div>
          </div>
        ) : (
          byHour.map(([hour, list]) => (
            <div className="hour-block" key={hour}>
              <div className="hour-block-hd">
                <div className="h serif">
                  {String(hour).padStart(2, "0")}
                  <span className="ampm">{hour < 12 ? "AM" : "PM"}</span>
                </div>
                <span className="line" />
                <span className="ct">
                  {list.length} {list.length === 1 ? "frame" : "frames"}
                </span>
              </div>
              <div className="grid">
                {list.map((shot) => (
                  <ImageCard
                    key={shot.imageId}
                    shot={shot}
                    focused={flashId === shot.imageId}
                    onChanged={onShotChanged}
                    onHidden={onShotHidden}
                    onOpen={(s) => setOpenShotId(s.imageId)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </main>

      {openShot && (
        <Lightbox
          shot={openShot}
          onClose={() => setOpenShotId(null)}
          onChanged={onShotChanged}
          onHidden={(id) => {
            // Advance past the hidden frame so the lightbox doesn't snap shut
            // mid-browse. If it was the last visible frame, just close.
            const i = displayedShots.findIndex((s) => s.imageId === id);
            const next =
              i >= 0 && i + 1 < displayedShots.length
                ? displayedShots[i + 1]
                : i > 0
                  ? displayedShots[i - 1]
                  : null;
            onShotHidden(id);
            setOpenShotId(next?.imageId ?? null);
          }}
          onPrev={openIndex > 0 ? () => setOpenShotId(displayedShots[openIndex - 1].imageId) : undefined}
          onNext={
            openIndex >= 0 && openIndex + 1 < displayedShots.length
              ? () => setOpenShotId(displayedShots[openIndex + 1].imageId)
              : undefined
          }
          onPrevDay={() => {
            setPendingJump("last");
            onSelectDate(addDays(selectedDate, -1));
          }}
          onNextDay={() => {
            setPendingJump("first");
            onSelectDate(addDays(selectedDate, 1));
          }}
        />
      )}
    </div>
  );
}

function DaySummary({ shots }: { shots: Shot[] }) {
  const counts: Record<Species | "untagged", number> = {
    "moose": 0,
    "roe-deer": 0,
    "wild-boar": 0,
    "other": 0,
    "untagged": 0,
  };
  for (const s of shots) {
    const sp = deriveSpecies(s.analysis);
    counts[sp ?? "untagged"]++;
  }
  const parts: string[] = [];
  for (const k of SPECIES_ORDER) {
    if (counts[k]) parts.push(`${counts[k]} ${SPECIES_LABEL[k].toLowerCase()}`);
  }
  if (counts.untagged) parts.push(`${counts.untagged} untagged`);

  return (
    <div
      className="meta"
      style={{
        fontFamily: "var(--mono)",
        fontSize: "0.75em",
        color: "var(--fg-mute)",
        marginBottom: "32px",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      {shots.length} {shots.length === 1 ? "frame" : "frames"} captured
      {parts.length > 0 && ` · ${parts.join(" · ")}`}
    </div>
  );
}
